import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// components and helpers
import * as dashboardActions from '../Workspace/Dashboard/DashboardActions';
import { workspaceActions } from '../Workspace/WorkspaceActions';
import WorkspaceList from './WorkspaceList';
import { activityQueries, caseQueries, fileQueries } from './helpers/queries';
import { BarChart, PieChart, Calendar } from '@acoer/ac-js-lib-charts';
import { transform } from '../Shared/helpers/ChartMappers';
import CreateWorkspaceDialog from './CreateWorkspaceDialog';
import totalCasesIcon from '../../assets/img/TotalCasesIcon.svg';
import totalFilesIcon from '../../assets/img/TotalFilesIcon.svg';
import ActivityIcon2 from '../../assets/img/ActivityIcon2.svg';
import PendingInvites from './PendingInvites';

// mui
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import AuditDrawer from '../Audit/AuditDrawer';
import Tooltip from '@mui/material/Tooltip';
import Fab from '@mui/material/Fab';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material/styles';
import Counts from '../Shared/Counts';
import { securityActions } from '../Workspace/Security/SecurityActions';
import { PaletteMode } from '@mui/material';

const HomeDashboard: React.FC = () => {
    const dispatch = useDispatch();
    const theme = useTheme();
    const dashboard = useSelector((state: any) => state.dashboard);
    const [openAudit, setOpenAudit] = useState(false);
    const [openDialog, setOpenDialog] = useState<boolean>(false);
    const [greeting, setGreeting] = useState('Good morning');
    const [openPendingInvites, setOpenPendingInvites] =
        useState<boolean>(false);
    const [pageLimit] = useState<number>(10);
    const [offset, setOffset] = useState<number>(0);
    const [sortField] = useState<string>('userId');
    const [sortDirection] = useState<string>('DESC');
    const currentUser = useSelector((state: any) => state.user.currentUser);
    const workspaceList = useSelector(
        (state: any) => state.workspace.workspaceList
    );
    const pendingInvitesList = useSelector(
        (state: any) => state.security.pendingInvitesList
    );

    useEffect(() => {
        const d = new Date();
        const time = d.getHours();
        if (time < 12) {
            setGreeting('Good morning,');
        } else if (time >= 12 && time < 17) {
            setGreeting('Good afternoon,');
        } else if (time >= 17) {
            setGreeting('Good evening,');
        }
    }, []);

    useEffect(() => {
        dispatch(workspaceActions?.resetWorkspace());
        dispatch(
            dashboardActions?.getDashboardDataMultiple(
                'CASE',
                '-all-',
                caseQueries
            )
        );
        dispatch(
            dashboardActions?.getDashboardDataMultiple(
                'FILE',
                '-all-',
                fileQueries
            )
        );
        dispatch(
            dashboardActions?.getDashboardDataMultiple(
                'ACTIVITY',
                '-all-',
                activityQueries
            )
        );
        dispatch(
            securityActions?.getPendingInvitations(
                pageLimit,
                offset,
                sortField,
                sortDirection
            )
        );
        dispatch(workspaceActions?.resetFileList());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (pendingInvitesList && pendingInvitesList.total > 0) {
            setOpenPendingInvites(true);
        }
    }, [pendingInvitesList]);

    const handleFetchMore = () => {
        const moreOffset = offset + 10;
        setOffset(moreOffset);
        dispatch(
            securityActions?.getPendingInvitations(
                pageLimit,
                moreOffset,
                sortField,
                sortDirection
            )
        );
    };

    const handleOpenAudit = () => {
        setOpenAudit(!openAudit);
    };

    const handleOpenDialog = () => {
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
    };

    const counts = [
        {
            icon: <img src={totalCasesIcon} alt="" width={40} />,
            title: 'Total Cases',
            color: 'linear-gradient(to right, #663299 40%, #66329999) !important',
            data:
                (dashboard &&
                    dashboard.caseList &&
                    dashboard.caseList.casesReportCounts &&
                    dashboard.caseList.casesReportCounts.items &&
                    dashboard.caseList.casesReportCounts.items[0] &&
                    dashboard.caseList.casesReportCounts.items[0].COUNT &&
                    dashboard.caseList.casesReportCounts.items[0].COUNT.toLocaleString()) ||
                0,
        },
        {
            icon: <img src={totalFilesIcon} alt="" width={40} />,
            title: 'Total Files',
            color: 'linear-gradient(to right, #325599 40%, #32559999) !important',
            data:
                (dashboard &&
                    dashboard.fileCount &&
                    dashboard.fileCount.importReportCounts &&
                    dashboard.fileCount.importReportCounts.items &&
                    dashboard.fileCount.importReportCounts.items[0] &&
                    dashboard.fileCount.importReportCounts.items[0].COUNT &&
                    dashboard.fileCount.importReportCounts.items[0].COUNT.toLocaleString()) ||
                0,
        },
        {
            icon: <img src={ActivityIcon2} alt="" width={50} />,
            title: 'Total Activities',
            color: 'linear-gradient(to right, #008577 40%, #00857799) !important',
            data:
                (dashboard &&
                    dashboard.activityCount &&
                    dashboard.activityCount.items &&
                    dashboard.activityCount.items[0] &&
                    dashboard.activityCount.items[0].COUNT &&
                    dashboard.activityCount.items[0].COUNT.toLocaleString()) ||
                0,
        },
    ];

    const palette = [
        '#325599',
        '#845EC2',
        '#009688',
        '#FF6B81',
        '#FFC46B',
        '#E388E5',
        '#7EC8E3',
        '#88E591',
        '#FF9357',
        '#ED4646',
    ];

    return (
        <Grid container spacing={1}>
            <Grid item>
                <Typography variant={'h5'}>
                    {greeting} {currentUser.firstName}!
                </Typography>
            </Grid>
            <Grid item xs={12}>
                <Grid container justifyContent={'flex-end'}>
                    <Grid item>
                        <Tooltip
                            title={`${
                                openAudit ? 'Close' : 'Open'
                            } Activity Log`}
                        >
                            <Fab
                                size="small"
                                aria-label="audit"
                                onClick={handleOpenAudit}
                                color={'secondary'}
                                sx={{
                                    position: 'fixed',
                                    top: (theme) => theme.spacing(10),
                                    right: (theme) => theme.spacing(2),
                                }}
                                data-testid="audit-fab"
                            >
                                {openAudit ? (
                                    <CloseIcon />
                                ) : (
                                    <img
                                        src={ActivityIcon2}
                                        alt=""
                                        height={'70%'}
                                    />
                                )}
                            </Fab>
                        </Tooltip>
                    </Grid>
                </Grid>
            </Grid>
            <Grid item xs={openAudit ? 9 : 12}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Grid container spacing={3} direction={'row'}>
                            {counts.map((count, i: number) => {
                                return (
                                    <Counts
                                        count={count}
                                        index={i}
                                        key={`home-counts-${i}`}
                                    />
                                );
                            })}
                        </Grid>
                    </Grid>
                    <Grid item xs={openPendingInvites ? 8 : 12}>
                        <WorkspaceList
                            handleOpenDialog={handleOpenDialog}
                            setOpenPendingInvites={setOpenPendingInvites}
                        />
                    </Grid>
                    {openPendingInvites && (
                        <Grid item xs={4}>
                            <PendingInvites
                                handleFetchMore={handleFetchMore}
                                setOpenPendingInvites={setOpenPendingInvites}
                            />
                        </Grid>
                    )}
                    <Grid item xs={4}>
                        <Paper elevation={0} sx={{ height: '100%', p: 2 }}>
                            <Typography variant={'h6'}>
                                Status of Imports
                            </Typography>
                            <PieChart
                                chartTitle={''}
                                // @ts-ignore
                                data={transform.pieChart(
                                    dashboard &&
                                        dashboard.fileCount &&
                                        dashboard.fileCount.importsPerStatus,
                                    'status'
                                )}
                                onDownloadCsv={() => console.log('hi')}
                                legendPosition={'bottom'}
                                showToolbar={false}
                                colourScheme={palette}
                                chartTheme={'dark'}
                            />
                        </Paper>
                    </Grid>
                    <Grid item xs={8}>
                        <Paper elevation={0} sx={{ height: '100%', p: 2 }}>
                            <Typography variant={'h6'}>
                                Cases per Workspace
                            </Typography>
                            <BarChart
                                chartTitle={''}
                                // @ts-ignore
                                data={
                                    transform.barChart(
                                        dashboard &&
                                            dashboard.caseList &&
                                            dashboard.caseList
                                                .casesPerWorkspace,
                                        'workspaceId',
                                        workspaceList.items
                                        // @ts-ignore
                                    ).data
                                }
                                // @ts-ignore
                                keys={
                                    transform.barChart(
                                        dashboard &&
                                            dashboard.caseList &&
                                            dashboard.caseList
                                                .casesPerWorkspace,
                                        'workspaceId'
                                        // @ts-ignore
                                    ).keys
                                }
                                onDownloadCsv={() => console.log('hi')}
                                layout={'horizontal'}
                                showToolbar={false}
                                xLegend={'Workspace'}
                                yLegend={'Count'}
                                chartTheme={theme.palette.mode}
                                colourScheme={palette}
                                withBrush
                            />
                        </Paper>
                    </Grid>
                    <Grid item xs={12}>
                        <Paper elevation={0} sx={{ p: 2, height: 420 }}>
                            <Typography variant={'h6'}>
                                Total Imports per Day (Last 365 Days)
                            </Typography>
                            <Calendar
                                chartTitle={''}
                                // @ts-ignore
                                data={transform.calendar(
                                    dashboard &&
                                        dashboard.fileCount &&
                                        dashboard.fileCount.importsPerDay,
                                    'dateToString_added'
                                )}
                                onDownloadCsv={() => console.log('hi')}
                                // @ts-ignore
                                chartTheme={theme.palette.mode as PaletteMode}
                            />
                        </Paper>
                    </Grid>
                </Grid>
            </Grid>
            {openAudit && (
                <Grid item xs={3}>
                    <Grid container>
                        <Grid item xs={12}>
                            <AuditDrawer workspaceId={null} />
                        </Grid>
                    </Grid>
                </Grid>
            )}
            <CreateWorkspaceDialog
                open={openDialog}
                handleCloseDialog={handleCloseDialog}
            />
        </Grid>
    );
};

export default HomeDashboard;
